import { useMemo } from 'react'
import useSharedCollectionWhere from '../../firebase/useSharedCollectionWhere'
import useSettings from '../../hooks/Settings'
import HouseVisitsHoursVisualization from '../dataVisualizations/HouseVisitsHoursVisualization'
import { buildCumulativeHoursSeries } from '../../helpers/dateHelpers/adressDashboard/tasksHours'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardOptions from './DashboardCardOptions'
import DashboardCardEmptyState from './DashboardCardEmptyState'
import { FILTER_EMPTY_STATE } from './dashboardEmptyStateConfig'
import { CATEGORICAL } from '../../helpers/chartPalette'
import useSharedCollection from '../../firebase/useSharedCollection'
import useDashboardCardSetting from '../../hooks/useDashboardCardSetting'
import { DASHBOARD_FILTER_FIELDS } from '../../helpers/dashboardHelpers/dashboardCardFilters'
import hasLocationFilter from '../../helpers/dashboardHelpers/hasLocationFilter'
import DashboardFilterBar from './DashboardFilterBar'
import DashboardLocationFilter from './DashboardLocationFilter'
import { taskInLocation, buildLocationByAdressId } from '../../helpers/locations/locationFilter'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Dashboard wrapper around the shared HouseVisitsHoursVisualization. Keeps
// the Firestore query + cumulative-series build; the chart leaf lives in
// the shared component so the live-report widget renders identically.

const DashboardHouseVisitsHoursCard = ({ item }) => {
    const { housevisits } = useSettings()
    // Locaties zijn één instelling, gedeeld met de deelnemers. De keuze wordt
    // per kaart bewaard op het DashboardItems-document, net als bij de
    // deelnemerkaarten. AI-CONTEXT: See docs/features/huishouden-locaties.md
    const locations = useSharedCollection('Locations')
    const [selectedLocationId, setSelectedLocationId] = useDashboardCardSetting(
        item,
        DASHBOARD_FILTER_FIELDS.SelectedLocationId,
        '',
    )
    const tasks = useSharedCollectionWhere('Tasks', 'Type', 'addressVisit')
    const adresses = useSharedCollection('Adresses')

    // Een huisbezoek draagt zelf geen locatie en erft hem van het huishouden.
    const locationByAdressId = useMemo(
        () => buildLocationByAdressId(adresses),
        [adresses],
    )
    // Vaste paletkleur. Deze kaart staat alleen op het dashboard, dus er valt
    // niets te kiezen; de opgeslagen ColorOverrides blijven in Firestore staan
    // maar worden niet meer gelezen. Zie docs/patterns.md#ui-chart-palette.
    const lineColor = CATEGORICAL[0]

    const cumulativeHoursData = useMemo(() => {
        const opLocatie = (Array.isArray(tasks) ? tasks : [])
            .filter((t) => taskInLocation(t, selectedLocationId, locationByAdressId))
        return buildCumulativeHoursSeries(opLocatie)
    }, [tasks, selectedLocationId, locationByAdressId])

    if (housevisits !== true) return null

    // Zolang de huishoudens nog onderweg zijn is `locationByAdressId` leeg, en
    // omdat een huisbezoek zijn locatie van het huishouden erft wijst het filter
    // dan ÉLK bezoek af. Zonder deze poort zegt de kaart 'nog geen uren' bij
    // elke keer laden -- en dat gebeurt telkens, want de keuze staat opgeslagen
    // op de kaart. AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const isLoading = tasks === undefined
        || (!!selectedLocationId && adresses === undefined)

    const isEmpty = !isLoading
        && (!Array.isArray(cumulativeHoursData) || cumulativeHoursData.length === 0)
    // 'Nog geen uren' is een bewering over de hele organisatie; met een filter
    // aan gaat het over één locatie.
    const isFilterEmpty = isEmpty && !!selectedLocationId

    return (
        <div className='housevisits-card-body'>
            <StaticCardLabel
                title='Cumulatieve uren gewerkt'
                pill={{ variant: 'housevisits', label: 'Huishoudens' }}
            />
            <DashboardFilterBar
                onClear={() => setSelectedLocationId('')}
                activeFilterCount={selectedLocationId ? 1 : 0}
                hasFilters={hasLocationFilter(locations)}
            >
                <DashboardLocationFilter
                    selectedLocationId={selectedLocationId}
                    onChange={setSelectedLocationId}
                />
            </DashboardFilterBar>
            <div className='housevisits-card-chart'>
                {isLoading ? null : isFilterEmpty ? (
                    <DashboardCardEmptyState
                        {...FILTER_EMPTY_STATE}
                        onCtaClick={() => setSelectedLocationId('')}
                    />
                ) : isEmpty ? (
                    <DashboardCardEmptyState type='housevisits-hours' />
                ) : (
                    <HouseVisitsHoursVisualization
                        data={cumulativeHoursData}
                        color={lineColor}
                        height={'100%'}
                    />
                )}
            </div>
            <DashboardCardOptions item={item} />
        </div>
    )
}

export default DashboardHouseVisitsHoursCard
